/**
 * Skynet · sk-announcement (módulo 8)
 * Cuenta atrás en cajas hasta una fecha y hora reales en una zona horaria (IANA).
 * Al llegar a cero, la barra entera desaparece (fuera del editor). Si la zona o la fecha no
 * son válidas, se quitan las cajas y queda solo el texto: nunca un contador inventado.
 * El cálculo de zona horaria es el mismo que el de sk-countdown.js (módulo 4).
 */
function partsIn(timeZone, epoch) {
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p = Object.fromEntries(f.formatToParts(new Date(epoch)).map((x) => [x.type, x.value]));
  return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour % 24, min: +p.minute, s: +p.second };
}

function zonedToEpoch(timeZone, y, m, d, h, min) {
  let guess = Date.UTC(y, m - 1, d, h, min);
  for (let i = 0; i < 2; i++) {
    const p = partsIn(timeZone, guess);
    const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min, p.s);
    guess -= asUtc - Date.UTC(y, m - 1, d, h, min);
  }
  return guess;
}

const pad = (n) => String(n).padStart(2, '0');

class SkAnnounceTimer extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.#stop();
    this.editor = this.dataset.editor === 'true';
    this.bar = this.closest('.sk-announce');
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{1,2}):(\d{2}):00$/.exec(this.dataset.end || '');
    let end = NaN;
    try {
      if (m) end = zonedToEpoch(this.dataset.tz || 'UTC', +m[1], +m[2], +m[3], +m[4], +m[5]);
    } catch (e) {
      end = NaN;
    }
    if (!Number.isFinite(end)) {
      this.remove(); // fecha o zona no válidas: solo texto
      return;
    }
    this.end = end;
    this.nums = Object.fromEntries([...this.querySelectorAll('[data-unit]')].map((b) => [b.dataset.unit, b.querySelector('[data-sk-num]')]));
    this.#tick();
    this.#timer = window.setInterval(this.#tick, 1000);
  }

  disconnectedCallback() {
    this.#stop();
  }

  #stop() {
    if (this.#timer) window.clearInterval(this.#timer);
    this.#timer = 0;
  }

  #tick = () => {
    const left = this.end - Date.now();
    if (left <= 0) {
      this.#stop();
      if (this.editor) {
        Object.values(this.nums).forEach((el) => el && (el.textContent = '00'));
      } else if (this.bar) {
        this.bar.hidden = true;
      }
      return;
    }
    const total = Math.floor(left / 1000);
    const values = {
      days: Math.floor(total / 86400),
      hours: Math.floor((total % 86400) / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60,
    };
    for (const [unit, el] of Object.entries(this.nums)) {
      const text = pad(values[unit] ?? 0);
      if (el && el.textContent !== text) el.textContent = text;
    }
  };
}

if (!customElements.get('sk-announce-timer')) customElements.define('sk-announce-timer', SkAnnounceTimer);

/**
 * Módulo 19 · rotación de mensajes de la barra.
 * - Cambia de mensaje cada data-interval segundos. Se para mientras el ratón está encima o el foco dentro
 *   (y vuelve al salir), con el botón de pausa (queda parada hasta volver a pulsarlo) y nunca arranca sola
 *   con «menos movimiento» ni en el editor. Flechas anterior/siguiente.
 * - aria-live «off» mientras rota sola; «polite» cuando la persona la maneja (patrón de carrusel WAI).
 * - Pestaña oculta: no rota (el temporizador solo avanza con la página visible).
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class SkAnnounceRotator extends HTMLElement {
  #ac = null;
  #timer = 0;
  #index = 0;
  #hover = false;
  #focus = false;
  #userPaused = false;

  connectedCallback() {
    this.#ac?.abort();
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.msgs = [...this.querySelectorAll('[data-sk-msg]')];
    this.live = this.querySelector('[data-sk-msgs]');
    this.prevBtn = this.querySelector('[data-sk-prev]');
    this.nextBtn = this.querySelector('[data-sk-next]');
    this.pauseBtn = this.querySelector('[data-sk-pause]');
    if (this.msgs.length < 2) return;
    this.interval = Math.max(3, Number(this.dataset.interval) || 5) * 1000;
    this.#userPaused = reducedMotion.matches || this.dataset.editor === 'true';
    [this.prevBtn, this.nextBtn, this.pauseBtn].forEach((b) => b && (b.hidden = false));
    this.prevBtn?.addEventListener('click', () => this.#manual(-1), { signal });
    this.nextBtn?.addEventListener('click', () => this.#manual(1), { signal });
    this.pauseBtn?.addEventListener('click', () => {
      this.#userPaused = !this.#userPaused;
      this.#sync();
    }, { signal });
    this.addEventListener('pointerenter', () => { this.#hover = true; this.#sync(); }, { signal });
    this.addEventListener('pointerleave', () => { this.#hover = false; this.#sync(); }, { signal });
    this.addEventListener('focusin', () => { this.#focus = true; this.#sync(); }, { signal });
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) { this.#focus = false; this.#sync(); }
    }, { signal });
    document.addEventListener('visibilitychange', () => this.#sync(), { signal });
    reducedMotion.addEventListener('change', () => { if (reducedMotion.matches) this.#userPaused = true; this.#sync(); }, { signal });
    // En el editor: al seleccionar la sección se queda en el primer mensaje (pausada).
    this.#show(0, false);
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#stop();
  }

  #stop() {
    if (this.#timer) window.clearInterval(this.#timer);
    this.#timer = 0;
  }

  #sync() {
    const running = !this.#userPaused && !this.#hover && !this.#focus && document.visibilityState === 'visible';
    if (this.pauseBtn) {
      this.pauseBtn.toggleAttribute('data-paused', this.#userPaused);
      this.pauseBtn.setAttribute('aria-label', this.#userPaused ? this.dataset.playLabel : this.dataset.pauseLabel);
    }
    if (this.live) this.live.setAttribute('aria-live', running ? 'off' : 'polite');
    if (running && !this.#timer) {
      this.#timer = window.setInterval(() => this.#show(this.#index + 1, true), this.interval);
    } else if (!running) {
      this.#stop();
    }
  }

  #manual(step) {
    this.#show(this.#index + step, !reducedMotion.matches);
    // Reinicia el tiempo del mensaje elegido.
    this.#stop();
    this.#sync();
  }

  #show(i, animate) {
    const n = this.msgs.length;
    this.#index = ((i % n) + n) % n;
    this.msgs.forEach((m, k) => {
      const on = k === this.#index;
      m.hidden = !on;
      m.toggleAttribute('data-sk-in', on && animate && !reducedMotion.matches);
    });
  }
}

if (!customElements.get('sk-announce-rotator')) customElements.define('sk-announce-rotator', SkAnnounceRotator);
